import { assertIsTruthy } from '@llx/common';
import { useEffect } from 'react';

import {
  ListMeetingLiveParticipantsDocument,
  ListMeetingLiveParticipantsQuery,
  ListMeetingLiveParticipantsQueryVariables,
  useJoinMeetingParticipantsMutation,
  useLeaveMeetingParticipantsMutation,
  useListMeetingLiveParticipantsQuery,
  useMeetingParticipantsHeartbeatMutation,
  useOnMeetingLiveParticipantsChangeSubscription,
} from '@/@generated/codegen/API';
import useBrowserLeaveCallback from '@/utils/helpers/hooks/useBrowserLeaveCallback';
import { captureSentry } from '@/utils/helpers/sentryHelper';

export const useLiveMeetingParticipants = (meetingID: string | null | undefined) => {
  const currentMeetingParticipantsQuery = useListMeetingLiveParticipantsQuery({
    variables: {
      meetingId: meetingID || '',
    },
    skip: !meetingID,
  });

  const [mutationJoinMeeting] = useJoinMeetingParticipantsMutation();
  const [mutationLeaveMeeting] = useLeaveMeetingParticipantsMutation();
  const [mutationParticipantHeartbeat] = useMeetingParticipantsHeartbeatMutation();

  useEffect(() => {
    if (!meetingID) return;
    void mutationJoinMeeting({
      variables: {
        meetingId: meetingID,
      },
    });
  }, [meetingID, mutationJoinMeeting]);

  useEffect(() => {
    if (!meetingID) return;

    const heartbeatInterval = setInterval(() => {
      void mutationParticipantHeartbeat({
        variables: {
          meetingId: meetingID,
        },
      });
    }, 20000);

    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [meetingID, mutationParticipantHeartbeat]);

  useBrowserLeaveCallback(() => {
    if (!meetingID) {
      // NOTE: This can happen when a browser crashes (ex: stress test script) so no sentry here
      return;
    }
    void mutationLeaveMeeting({
      variables: {
        meetingId: meetingID,
      },
    });
  });

  useOnMeetingLiveParticipantsChangeSubscription({
    variables: {
      meetingId: meetingID || '',
    },
    skip: !meetingID,
    onData: ({ data, client }) => {
      const obj = data.data?.onMeetingLiveParticipantsChange;
      if (!obj) {
        captureSentry({
          title: 'useLiveMeetingParticipants | onData | obj is undefined',
          detail: {},
        });
        return;
      }
      assertIsTruthy(meetingID);

      const existingData = client.readQuery<
        ListMeetingLiveParticipantsQuery,
        ListMeetingLiveParticipantsQueryVariables
      >({
        query: ListMeetingLiveParticipantsDocument,
        variables: {
          meetingId: meetingID,
        },
      });
      if (!existingData) return;
      let updatedParticipants = existingData.listMeetingLiveParticipants.participants;
      switch (obj.type) {
        case 'JOINED':
          if (!updatedParticipants.find((u) => u.id === obj.user.id)) {
            updatedParticipants = [...updatedParticipants, obj.user];
          }
          break;
        case 'LEFT':
          updatedParticipants = updatedParticipants.filter((u) => u.id !== obj.user.id);
          break;
      }
      client.writeQuery({
        query: ListMeetingLiveParticipantsDocument,
        variables: {
          meetingId: meetingID,
        },
        data: {
          listMeetingLiveParticipants: {
            ...existingData.listMeetingLiveParticipants,
            participants: updatedParticipants,
            count: updatedParticipants.length,
          },
        },
      });
    },
  });

  return currentMeetingParticipantsQuery.data?.listMeetingLiveParticipants.participants || [];
};
